import React, { useEffect } from "react";

import placeholderCover from "./assets/thegap_party1.jpg";
import receiptBackground from "./assets/FestFolk.jpg";

import {
  Alert,
  Area,
  BookTableBox,
  ChooseDateSelector,
  Container,
  DescriptionCard,
  DisabledOverlay,
  FloatingBackButton,
  GroupSizePicker,
  Input,
  LazyCoverImage,
  LoadingOverlay,
  PageCover,
  QuantityControls,
  ReceiptLine,
  SelectHighlighter,
  SelectValue,
  Skeleton,
  Summary,
  TitledSection,
  ErrorBorder,
  ErrorHint,
  useHiddenTabBar,
  useNow,
  upsertQuantity,
  ProductsCarousel,
  TextField,
  ConfirmStage,
} from "@greeter/matter";
import {
  Bundle,
  CreatedBy,
  FloorPlan,
  GreeterEvent,
  SpecialOpeningHours,
  TableId,
  Venue,
} from "@greeter/core";
import {
  IonContent,
  IonPage,
  IonSpinner,
  useIonViewWillEnter,
} from "@ionic/react";
import {
  Maybe,
  classNames,
  doNothing,
  getServiceDayPeriod,
} from "@greeter/util";
import { LenientDatePeriod, TimeOfDay } from "@greeter/date";
import { useCallback, useMemo, useRef, useState } from "react";
import { getEnv } from "@greeter-venue/utility/ConfigHooks";
import { Query } from "@greeter/query";
import { InvalidValue } from "./EditBookingPageApiHandler";
import { getBookingPeriod2 } from "./util";
import { addMinutes, differenceInMinutes, isSameDay, set } from "date-fns";
import { useInitEffect } from "@greeter/hooks";

import css from "./EditBookingPage.module.scss";

function noInvalidValues(invalidValues: InvalidValue<unknown>[]) {
  return !invalidValues || invalidValues.length === 0;
}

const useScrollToTopOnWillEnter = () => {
  const contentRef = useRef<HTMLIonContentElement>(null);
  useIonViewWillEnter(() => {
    contentRef.current?.scrollToTop();
  });
  return contentRef;
};

function hasError(id: string, invalidValues: InvalidValue<unknown>[]) {
  return !!invalidValues.find((v) => v.elementId === id);
}

export type EditBookingPageProps = React.PropsWithChildren & {
  venue?: Query<Venue>;

  mode?: "edit" | "create";

  onProductChange?: (product: Bundle) => void;
  product: Query<Maybe<Bundle>>;
  products: Query<Array<Bundle>>;

  period?: LenientDatePeriod;
  onPeriodChange?: (
    bookingPeriod: LenientDatePeriod,
    lookupPeriod: LenientDatePeriod,
  ) => void;

  onCommentChange?: (comment: string) => void;
  comment?: Query<string>;

  onGroupSizeChange?: (groupSize: number) => void;
  groupSize?: Query<number>;

  greeterEvents?: Query<GreeterEvent[]>;

  specialOpeningHours?: Query<Array<SpecialOpeningHours>>;

  /**
   * Tables currently booked by the booking,
   * only relevant for bookings related to edits
   */
  currentlyBookedTables?: Query<TableId[]>;

  bookedTables?: Query<TableId[]>;
  lockedTables?: Query<TableId[]>;
  onSelectedTablesChange?: (t: TableId[]) => void;
  selectedTables?: Query<TableId[]>;

  floorPlan?: Query<FloorPlan>;

  customerName: Query<string>;
  onCustomerNameChange?: (name: string) => void;
  customerEmail: Query<string>;
  onCustomerEmailChange?: (email: string) => void;
  customerPhoneNumber: Query<string>;
  onCustomerPhoneNumberChange?: (phoneNumber: string) => void;

  onValidate?: () => Promise<InvalidValue<any>[]>;
  onCreate?: () => Promise<void>;
  onSave?: () => Promise<void>;

  orderLines?: Query<ReceiptLine[]>;
  onOrderLinesChange?: (lines: ReceiptLine[]) => void;

  createdBy?: CreatedBy;

  invalidValues?: Array<InvalidValue<any>>;
};

const empty = Query.done([]);

export function EditBookingPage({
  venue,
  product,
  products = empty,
  onProductChange = doNothing,

  mode,

  comment = Query.done(""),
  onCommentChange = doNothing,

  onGroupSizeChange = doNothing,
  groupSize = Query.done(2),

  greeterEvents = empty,

  // onArrivalDateChange = doNothing,
  // arrivalDate,

  // onArrivalTimeChange = doNothing,
  // arrivalTimes = Query.done([]),
  // arrivalTime,

  period,
  onPeriodChange,

  specialOpeningHours,

  currentlyBookedTables = empty,

  bookedTables = empty,
  lockedTables = empty,
  onSelectedTablesChange = doNothing,
  selectedTables = empty,

  floorPlan,

  customerName = Query.done(""),
  onCustomerNameChange = doNothing,
  customerEmail = Query.done(""),
  onCustomerEmailChange = doNothing,
  customerPhoneNumber = Query.done(""),
  onCustomerPhoneNumberChange = doNothing,

  onValidate,
  onCreate,
  onSave,

  orderLines = empty,
  onOrderLinesChange = doNothing,

  createdBy,

  invalidValues = [],
}: EditBookingPageProps) {
  useHiddenTabBar();

  const contentRef = useScrollToTopOnWillEnter();

  const [confirmStage, setConfirmStage] = useState<ConfirmStage>("unconfirmed");

  const quantity = useMemo(() => {
    if (orderLines.type !== "done" || product?.type !== "done") return 0;

    return (
      orderLines.data.find((ol) => ol.productId === product?.data?.id)
        ?.quantity ?? 0
    );
  }, [orderLines, product]);

  const [bookableDates, setBookableDates] = useState<LenientDatePeriod[]>([]);
  useInitEffect(
    () => {
      if (
        venue?.type === "done" &&
        specialOpeningHours?.type === "done" &&
        bookableDates.length === 0
      ) {
        const periods = Venue.createDatePeriodsFromOpeningHours({
          now: set(new Date(), {
            hours: 0,
            minutes: 0,
            seconds: 0,
            milliseconds: 0,
          }),
          range: 360,
          openingHours: venue.data.bookableOpeningHours,
          specialOpeningHours: specialOpeningHours
            ? Query.unwrapOr(specialOpeningHours, [])
            : [],
        });
        setBookableDates(periods);
      }
    },
    [venue, specialOpeningHours],
    () => {
      return venue?.type === "done" && specialOpeningHours?.type === "done";
    },
  );

  const handleQuantityChange = useCallback(
    (quantity: number) => {
      if (
        product &&
        orderLines.type === "done" &&
        product.type === "done" &&
        product.data
      )
        onOrderLinesChange(
          upsertQuantity(
            orderLines.data,
            product.data.id,
            product.data.title,
            product.data.price,
            quantity,
          ),
        );
    },
    [onOrderLinesChange, orderLines, product],
  );

  const [arrivalDate, setArrivalDate] = useState<Date>();
  const [arrivalDateTime, setArrivalDateTime] = useState<Date>();

  useInitEffect(
    () => {
      if (!period) return;

      const _period = bookableDates.find((p) => p.isWithin(period?.from));
      if (!_period) return;

      setArrivalDate(_period.from);
      setArrivalDateTime(period.from);
    },
    [period, bookableDates],
    () => !!period && bookableDates.length > 0,
  );

  const arrivalTimes = useMemo(() => {
    if (!arrivalDate) return [];

    const timeofdays: Array<Date> = [];

    const _period = bookableDates.find((p) => isSameDay(p.from, arrivalDate));

    if (!_period) return [];

    for (
      let i = 0, end = differenceInMinutes(_period.to, _period.from);
      i <= end;
      i += 15
    ) {
      timeofdays.push(addMinutes(_period.from, i));
    }

    return timeofdays;
  }, [bookableDates, arrivalDate]);

  const selectableArrivalTimes: Array<SelectValue<Date>> = useMemo(() => {
    return arrivalTimes.map((d) => {
      const tod = TimeOfDay.fromDate(d);
      return { label: tod.toString(false), value: d };
    });
  }, [arrivalTimes]);

  const handleGroupSizeChange = useCallback(
    (val: number) => onGroupSizeChange(val),
    [onGroupSizeChange],
  );

  const handleCommentChange = useCallback(
    (e: any) => onCommentChange(e.currentTarget.value),
    [onCommentChange],
  );

  const handleCustomerNameChanged = useCallback(
    (e: any) => onCustomerNameChange(e.target.value),
    [onCustomerNameChange],
  );

  const handleCustomEmailChanged = useCallback(
    (e: any) => onCustomerEmailChange(e.target.value),
    [onCustomerEmailChange],
  );

  const handleCustomePhoneNumberChanged = useCallback(
    (e: any) => onCustomerPhoneNumberChange(e.target.value),
    [onCustomerPhoneNumberChange],
  );

  const [validating, setValidating] = useState(false);

  useEffect(() => {
    async function continueIfValid() {}

    continueIfValid();
  }, [invalidValues, validating, confirmStage]);

  // NOTE: We split this into a callback and an effect
  //       so that we can display invalid values.
  //       By abstracting it to the confirming state
  //       we can ensure that all values are valid before
  //       continuing into the actual confirm stage.
  const confirm = useCallback(async () => {
    const invalidValues = (await onValidate?.()) ?? [];
    try {
      if (noInvalidValues(invalidValues)) {
        setConfirmStage("confirming");
        if (mode === "edit") {
          await onSave?.();
        } else if (mode === "create") {
          await onCreate?.();
        }
        setTimeout(() => setConfirmStage("done"), 1_000);
        setTimeout(() => setConfirmStage("unconfirmed"), 1_000);
      } else {
        setConfirmStage("unconfirmed");
      }
    } catch (error) {
      console.error(error);
      setConfirmStage("unconfirmed");
    }
  }, [onCreate, onValidate, invalidValues]);

  const env = getEnv();

  useEffect(() => {
    const invalidValuesWithElements = invalidValues
      .filter((iv) => iv.elementId)
      .map((iv) => document.querySelector(`#${iv.elementId}`))[0];

    if (invalidValuesWithElements) {
      invalidValuesWithElements.scrollIntoView({ behavior: "smooth" });
    }
  }, [invalidValues]);

  useEffect(() => {
    const arrival = arrivalDateTime ?? arrivalTimes?.[0];
    if (arrival && bookableDates && onPeriodChange) {
      const lookupPeriod = getServiceDayPeriod({
        bookingArrivalTime: arrival,
      });
      const bookingPeriodResult = getBookingPeriod2(bookableDates, arrival);
      if (bookingPeriodResult.ok) {
        onPeriodChange(bookingPeriodResult.value, lookupPeriod);
      }
    }
  }, [arrivalDateTime, bookableDates, arrivalTimes]);

  const unavailableTables = useMemo(() => {
    return [
      ...Query.unwrapOr(bookedTables, []),
      ...Query.unwrapOr(lockedTables, []),
    ];
  }, [bookedTables, lockedTables]);

  const now = useNow();

  return (
    <IonPage>
      <FloatingBackButton />
      <IonContent ref={contentRef}>
        <Container className={css.Container}>
          <PageCover>
            <LazyCoverImage src={placeholderCover} className={css.CoverImage} />
          </PageCover>
          <div className={css.VenueLogo}>
            {venue && venue.type === "done" ? (
              <img src={venue.data.logoUrl} alt="Venue logo" />
            ) : (
              <div />
            )}
          </div>
          <DescriptionCard className={css.DescriptionCard}>
            <ErrorBorder error={hasError("product", invalidValues)}>
              {hasError("product", invalidValues) && (
                <ErrorHint
                  style={{
                    textAlign: "center",
                    width: "100%",
                    display: "inline-block",
                  }}
                >
                  Der skal være min. 1 produkt til bookingen.
                </ErrorHint>
              )}
              <TitledSection
                id="product"
                title="Vælg drikkevarer"
                {...classNames(css.ChooseProductSection)}
                center
              >
                <div
                  style={{
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    gap: "2rem",
                  }}
                >
                  <ProductsCarousel
                    specialPricePeriods={[]}
                    product={product.type === "done" ? product.data : undefined}
                    products={products.type === "done" ? products.data : []}
                    onProductChanged={
                      (b) =>
                        onProductChange(
                          b as Bundle,
                        ) /* TODO: THIS SHOULD BE BUNDLES OR IDS */
                    }
                  />
                  <div style={{ display: "grid", placeItems: "center" }}>
                    <QuantityControls
                      onChange={handleQuantityChange}
                      quantity={quantity}
                    />
                  </div>
                </div>
                {(product.type === "loading" ||
                  products.type === "loading") && <LoadingOverlay />}
                {createdBy === "customer" && (
                  <DisabledOverlay>
                    <Alert
                      title="Oprettet af gæst"
                      description="Du kan ikke ændre i ordren hvis den er oprettet af gæsten"
                    />
                  </DisabledOverlay>
                )}
              </TitledSection>
            </ErrorBorder>
            <TitledSection
              title="Kommentar"
              className={css.TitledSection}
              hint="Eks. valg af vodka eller juice"
            >
              <Area>
                {comment.type === "done" && (
                  <TextField
                    onChange={handleCommentChange}
                    value={comment.data}
                    placeholder="Stik mig en Små Fugle og et Sommersby tårn "
                    style={{ backgroundColor: "var(--gm-color-default-bg)" }}
                  />
                )}
              </Area>
            </TitledSection>

            <TitledSection
              id="groupsize"
              title="Antal personer?"
              {...classNames(
                css.TitledSection,
                hasError("groupsize", invalidValues) && css.ErrorBorder,
              )}
            >
              {groupSize.type === "done" && (
                <GroupSizePicker
                  data-testid="groupsizes"
                  value={groupSize.data}
                  onChange={handleGroupSizeChange}
                />
              )}
            </TitledSection>
            <div className={css.SectionHR} />
            <TitledSection
              id="arrival-date"
              title="Vælg en aften"
              {...classNames(
                css.TitledSection,
                hasError("arrival-date", invalidValues) && css.ErrorBorder,
              )}
            >
              {!venue || venue?.type === "loading" ? (
                <Skeleton style={{ width: "100%", height: "6rem" }} />
              ) : (
                specialOpeningHours?.type === "done" &&
                greeterEvents.type === "done" &&
                venue.type === "done" && (
                  <ChooseDateSelector
                    onDateChanged={setArrivalDate}
                    className={css.SingleSelectButtonBar}
                    btnClassName={css.SelectButton}
                    dates={bookableDates}
                    date={arrivalDate}
                    now={now}
                    greeterEvents={greeterEvents.data}
                  />
                )
              )}
              {Query.isLoading(venue) && <LoadingOverlay />}
            </TitledSection>
            <div className={css.SectionHR} />
            <TitledSection
              id="arrival-time"
              title="Ankomst tid?"
              {...classNames(
                css.TitledSection,
                hasError("arrival-time", invalidValues) && css.ErrorBorder,
              )}
            >
              <SelectHighlighter
                className={css.SingleSelectButtonBar}
                activeClassName={css.Active}
                btnClassName={css.SelectButton}
                selectableValues={selectableArrivalTimes}
                selectedValue={arrivalDateTime}
                onSelect={(s) => setArrivalDateTime(s.value)}
              />
              {Query.isLoading(venue) && <LoadingOverlay />}
            </TitledSection>
            <div className={css.SectionHR} />

            <ErrorBorder error={hasError("customer-name", invalidValues)}>
              <TitledSection
                id="customer-name"
                title="* Kundens navn"
                // {...classNames(
                //   hasError("customer-name", invalidValues) && css.ErrorBorder
                // )}
              >
                {customerName.type === "done" && (
                  <Input
                    style={{
                      backgroundColor: "var(--gm-color-default-bg)",
                      margin: "0 1rem",
                    }}
                    type="text"
                    placeholder=""
                    value={customerName.data}
                    onChange={handleCustomerNameChanged}
                  />
                )}
                {hasError("customer-name", invalidValues) && (
                  <ErrorHint>Vi skal bruge et navn på gæsten...</ErrorHint>
                )}
              </TitledSection>
            </ErrorBorder>
            <TitledSection title="Kundens email">
              {customerEmail?.type === "done" && (
                <Input
                  style={{
                    backgroundColor: "var(--gm-color-default-bg)",
                    margin: "0 1rem",
                  }}
                  type="text"
                  placeholder=""
                  value={customerEmail.data}
                  onChange={handleCustomEmailChanged}
                />
              )}
            </TitledSection>
            <TitledSection title="Kundens tlf. nummer">
              {customerPhoneNumber?.type === "done" && (
                <Input
                  style={{
                    backgroundColor: "var(--gm-color-default-bg)",
                    margin: "0 1rem",
                  }}
                  type="text"
                  placeholder=""
                  value={customerPhoneNumber.data}
                  onChange={handleCustomePhoneNumberChanged}
                />
              )}
            </TitledSection>
          </DescriptionCard>
        </Container>
        <Container>
          <ErrorBorder error={hasError("tables", invalidValues)}>
            <TitledSection
              id="tables"
              className={css.BookTableBox}
              title="Vælg pladser"
            >
              <Area>
                {!floorPlan ? (
                  <Skeleton
                    pulse
                    style={{
                      width: "100%",
                      margin: "0",
                      aspectRatio: "1 / 1",
                      boxSizing: "border-box",
                    }}
                  />
                ) : (
                  bookedTables.type === "done" &&
                  lockedTables.type === "done" &&
                  floorPlan.type === "done" &&
                  groupSize.type === "done" &&
                  selectedTables.type === "done" && (
                    <BookTableBox
                      env={env}
                      bookedTables={unavailableTables}
                      floorPlan={floorPlan.data}
                      groupSize={groupSize.data}
                      selectedTables={selectedTables.data}
                      selectableBookedTables={Query.unwrap(
                        currentlyBookedTables,
                      )}
                      display="number"
                      onSelectedTablesChange={onSelectedTablesChange}
                      featureFlags={{ overlayOnOpeningHoursViolation: false }}
                    />
                  )
                )}
                {!floorPlan && <LoadingOverlay />}
              </Area>
              {hasError("tables", invalidValues) && (
                <ErrorHint>Du skal vælge min. 1 bord</ErrorHint>
              )}
            </TitledSection>
          </ErrorBorder>
        </Container>
        <Container {...classNames(css.FinishOrderContainer, css.OverlapFix)}>
          <LazyCoverImage
            styles={{ container: { opacity: 0.3 } }}
            className={css.Background}
            src={receiptBackground}
          />
          <Container className={css.InnerContainer}>
            <Summary
              lines={orderLines?.type === "done" ? orderLines.data : []}
            />
            {orderLines?.type === "loading" && (
              <LoadingOverlay message="Henter..." />
            )}
          </Container>
        </Container>

        <Container style={{ paddingTop: "1rem", paddingBottom: "1rem" }}>
          <button
            data-testid="create-booking-btn"
            style={{
              background: "var(--gm-color-gradient-bg)",
              fontSize: "1.75rem",
              fontWeight: "bold",
              textAlign: "center",
              color: "white",
              borderRadius: "10px",
              width: "calc(100% - 20px)",
              padding: "1rem",
              margin: "auto",
              display: "flex",
              flexDirection: "row",
              gap: 5,
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={confirm}
          >
            {confirmStage !== "unconfirmed" && (
              <IonSpinner name="lines-small" />
            )}
            Tryk for at gemme
          </button>
        </Container>
      </IonContent>
    </IonPage>
  );
}
